import { createRoot } from 'react-dom/client'
import { ErrorBoundary } from 'react-error-boundary'
import { HotkeysProvider } from 'react-hotkeys-hook'
import { BrowserRouter as Router } from 'react-router-dom'
import 'allotment/dist/style.css'
import axios from 'axios'
import { enableMapSet, setAutoFreeze } from 'immer'
import { QueryParamProvider } from 'use-query-params'
import { ReactRouter6Adapter } from 'use-query-params/adapters/react-router-6'
import { CssBaseline } from '@mui/material'
import { ThemeProvider } from '@mui/material/styles'
import { LicenseInfo } from '@mui/x-license'
import '@fontsource/roboto/300.css'
import '@fontsource/roboto/400.css'
import '@fontsource/roboto/500.css'
import '@fontsource/roboto/700.css'
import { QueryClientProvider } from '@tanstack/react-query'
import { GenericErrorBoundaryFallback } from '@ui/errors'
import { SnackbarKey, SnackbarProvider } from '@ui/snackbar'
import { AuthenticationProvider } from 'src/components/contexts'
import ExpandableSnackbarContent from 'src/components/ui/snackbar/ExpandableSnackbarContent'
import SnackbarCloseButton from 'src/components/ui/snackbar/SnackbarCloseButton'
import queryClient from 'src/state/client'
import { theme } from 'src/styles'
import App from './App'
import { appConfig } from './constants'
import './styles/default.css'

setAutoFreeze(false)

LicenseInfo.setLicenseKey(appConfig.muiXProLicense as string)

// tell immer to create a new Set / Map when mutating an existing Set / Map
// within produce(), more context here: https://immerjs.github.io/immer/map-set
enableMapSet()

const closeNotificationAction = (key: SnackbarKey) => <SnackbarCloseButton snackbarKey={key} />

if (appConfig.useNgrok) {
  axios.interceptors.request.use(config => {
    if (config.headers) {
      config.headers['ngrok-skip-browser-warning'] = 'true'
    }
    return config
  })
}

const container = document.getElementById('root')
const root = createRoot(container as HTMLElement)

root.render(
  <ErrorBoundary FallbackComponent={GenericErrorBoundaryFallback}>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <SnackbarProvider
          autoHideDuration={10000}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          action={closeNotificationAction}
          Components={{
            default: ExpandableSnackbarContent,
            error: ExpandableSnackbarContent,
            info: ExpandableSnackbarContent,
            success: ExpandableSnackbarContent,
            warning: ExpandableSnackbarContent,
          }}
        >
          <Router>
            <QueryParamProvider adapter={ReactRouter6Adapter}>
              <AuthenticationProvider>
                <HotkeysProvider initiallyActiveScopes={['*']}>
                  <App />
                </HotkeysProvider>
              </AuthenticationProvider>
            </QueryParamProvider>
          </Router>
        </SnackbarProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </ErrorBoundary>,
)
